import moment from 'moment'
import 'moment-timezone'
import { formatInTimeZone } from 'date-fns-tz'
import { isEmpty } from 'lodash'
import i18next from 'i18next'

import { BERLIN_TIME_ZONE, DEFAULT_HUMAN_TIME } from '../const'
import { addSeconds, differenceInSeconds, format, intervalToDuration, parse, startOfDay } from 'date-fns'

/**
 * Get the current timestamp in milliseconds.
 */
export const getCurrentTimestamp = (): number => new Date().getTime()

/**
 * Get formatted hours by international number format by the browsers language
 * @param hours
 */
export const getFormattedHours = (hours: number): string => Intl.NumberFormat(navigator.language, { maximumFractionDigits: 0 }).format(hours)

/**
 * Get the hour string from a date object.
 * @param date
 * @returns
 */
export function getEnglishHourString(date: Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  })
    .format(date)
    .replace(':', '.')
}

/**
 * Returns a Date object representing the same local time, but with the specified IANA timezone.
 * The returned Date is the UTC time that corresponds to the specified time in the desired timezone.
 */
export const setTimeZoneKeepLocalTime = (date: Date, time?: Date, timeZone?: string): Date => {
  // Build local date and time string
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hour = String((time ?? date).getHours()).padStart(2, '0')
  const minute = String((time ?? date).getMinutes()).padStart(2, '0')
  const second = '00'

  const localString = `${year}-${month}-${day} ${hour}:${minute}:${second}`

  // Create moment in target timezone, keeping local time
  const m = moment.tz(localString, 'YYYY-MM-DD HH:mm:ss', timeZone ?? BERLIN_TIME_ZONE)
  return m.toDate()
}

export const dateToHumanTimeWithoutUtcOffset = (value: Date, dateFormat: string): string => {
  return formatInTimeZone(value, 'UTC', dateFormat)
}

export const humanTimeToSecondsFromMidnight = (humanTime: string, timeFormat = DEFAULT_HUMAN_TIME): number => {
  if (isEmpty(humanTime)) return 0

  const midnight = startOfDay(new Date())
  const timeOnDay = parse(humanTime, timeFormat, new Date())

  return differenceInSeconds(timeOnDay, midnight)
}

export const secondsFromMidnightToHumanTime = (secondsFromMidnight: number, timeFormat = DEFAULT_HUMAN_TIME): string => {
  const midnight = startOfDay(new Date())
  const timeDate = addSeconds(midnight, secondsFromMidnight)
  return format(timeDate, timeFormat)
}

export const formatDuration = (durationMilliseconds: number): string => {
  const duration = intervalToDuration({ start: new Date(0), end: new Date(durationMilliseconds) })

  const days = duration.days || 0
  const hours = duration.hours || 0
  const minutes = duration.minutes || 0
  const seconds = duration.seconds || 0

  if (days > 0) {
    // format: 1d 21h 3m 43s
    return i18next.t('common.format.timePreciseShorthandDaysIncluded', { days: days, hours: hours, mins: minutes, sec: seconds })
  }

  // format: 0h 3m 43s
  return i18next.t('common.format.timePreciseShorthand', { hours: hours, mins: minutes, sec: seconds })
}
